import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Coins,
  Wallet,
  Zap,
  Image,
  ShoppingBag,
  Users,
  TrendingUp,
  Clock,
  Sparkles,
  Shield,
  RefreshCw,
  ExternalLink,
  Code2,
  ArrowRight,
  FolderOpen,
  CheckCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useWalletSafe } from "@/hooks/useWalletSafe";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useNavigate } from "react-router-dom";
import Footer from "@/components/Footer";
import AppHeader from "@/components/AppHeader";
import { MobileWalletConnect } from "@/components/MobileWalletConnect";
import { NFTCard, NFTCardSkeleton } from "@/components/marketplace/NFTCard";
import { NFTCarousel } from "@/components/marketplace/NFTCarousel";
import { useMarketplaceNFTs, MarketplaceNFT } from "@/hooks/useMarketplaceNFTs";
import { TopCreatorsLeaderboard } from "@/components/leaderboard/TopCreatorsLeaderboard";
import { CollectionCarousel } from "@/components/marketplace/CollectionCarousel";

import { isMobileDevice } from "@/lib/mobileWalletFix";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProgramIdDisplay, SolanaExplorerLink } from "@/components/SolanaExplorerLink";
import { EMON_NFT_PROGRAM_ID } from "@/integrations/anchor/emon-nft-bonding-idl";
import { EmonPriceIndicator } from "@/components/EmonPriceIndicator";
import { priceToUsd, formatUsdPrice } from "@/lib/priceUtils";
import { useSolPrice } from "@/hooks/useSolPrice";
import { PageHero } from "@/components/PageHero";

export default function Index(): JSX.Element {
  const { connected, publicKey, disconnect: walletDisconnect } = useWalletSafe();
  const { nfts, allNfts, collections, loading: nftsLoading, refresh: refetchMarketplaceNFTs } = useMarketplaceNFTs();
  const { usdToSol } = useSolPrice();

  const [showMobileWallet, setShowMobileWallet] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [stats, setStats] = useState({ nfts: 0, users: 0, volume: 0 });
  const [activeTab, setActiveTab] = useState("trending");
  const [dynamicMessage, setDynamicMessage] = useState("");

  const formatVolumeDisplay = (volume: number) => {
    const usdValue = priceToUsd(volume);
    const solValue = usdToSol(usdValue);
    return { usd: formatUsdPrice(usdValue), sol: `≈ ${solValue.toFixed(4)} SOL` };
  };
  const navigate = useNavigate();

  const trendingNFTs = useMemo(() => {
    return [...allNfts]
      .sort((a, b) => {
        const scoreA = a.sales_count * 10 + a.total_earned * 100;
        const scoreB = b.sales_count * 10 + b.total_earned * 100;
        return scoreB - scoreA;
      })
      .slice(0, 8);
  }, [allNfts]);

  const highValueNFTs = useMemo(() => {
    return [...allNfts]
      .filter((nft) => nft.sales_count > 0)
      .sort((a, b) => (b.current_price || 0) - (a.current_price || 0))
      .slice(0, 8);
  }, [allNfts]);

  const recentNFTs = useMemo(() => {
    return [...allNfts].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 8);
  }, [allNfts]);

  const collectionNFTs = useMemo(() => {
    const topCollections = collections.slice(0, 6);
    return topCollections.map((col) => ({
      collection: col,
      nfts: allNfts.filter((nft) => nft.collection_name === col.collection_name),
    }));
  }, [allNfts, collections]);

  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  useEffect(() => {
    const messages = [
      `${trendingNFTs.length} trending NFTs available now`,
      `${highValueNFTs.length} high-value NFTs in the market`,
      `${recentNFTs.length} recently created artworks`,
      `All NFTs verified for authenticity`,
    ];
    let index = 0;
    setDynamicMessage(messages[0]);
    const interval = setInterval(() => {
      index = (index + 1) % messages.length;
      setDynamicMessage(messages[index]);
    }, 4000);
    return () => clearInterval(interval);
  }, [trendingNFTs.length, highValueNFTs.length, recentNFTs.length]);

  useEffect(() => {
    const fetchStats = async () => {
      const { data: platformStats } = await supabase.rpc("get_platform_stats_secure");
      if (platformStats && platformStats.length > 0) {
        const ps = platformStats[0];
        setStats({ nfts: ps.total_nfts || 0, users: ps.total_users || 0, volume: ps.total_volume || 0 });
      } else {
        const { count: nftCount } = await supabase.from("nfts").select("*", { count: "exact", head: true }).eq("is_listed", true);
        const { data: volumeData } = await supabase.from("nfts").select("total_earned");
        const totalVolume = volumeData?.reduce((sum, n) => sum + (n.total_earned || 0), 0) || 0;
        setStats({ nfts: nftCount || 0, users: 0, volume: totalVolume });
      }
    };
    fetchStats();
    const channel = supabase
      .channel("landing-stats")
      .on("postgres_changes", { event: "*", schema: "public", table: "nfts" }, fetchStats)
      .on("postgres_changes", { event: "*", schema: "public", table: "profiles" }, fetchStats)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    const nftPriceChannel = supabase
      .channel("public-nft-price-updates")
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "nfts", filter: "is_listed=eq.true" }, () => refetchMarketplaceNFTs())
      .subscribe();
    return () => { supabase.removeChannel(nftPriceChannel); };
  }, [refetchMarketplaceNFTs]);

  // Session sync is handled by useWalletSupabaseSession in AppHeader.
  // No duplicate auth flow here — prevents repeated Phantom signature requests.

  const handleDisconnect = async () => {
    await walletDisconnect();
    await supabase.auth.signOut();
    toast({ title: "Disconnected", description: "Wallet disconnected successfully" });
  };

  const renderNFTSection = (nftList: MarketplaceNFT[], showTrending = false) => {
    if (nftsLoading) {
      return (
        <>
          <div className="md:hidden"><NFTCarousel nfts={[]} loading={true} /></div>
          <div className="hidden md:grid grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (<NFTCardSkeleton key={i} />))}
          </div>
        </>
      );
    }
    if (nftList.length === 0) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No NFTs available yet</p>
        </div>
      );
    }
    return (
      <>
        <div className="md:hidden"><NFTCarousel nfts={nftList} showTrending={showTrending} /></div>
        <div className="hidden md:grid grid-cols-3 lg:grid-cols-4 gap-4">
          {nftList.map((nft) => (<NFTCard key={nft.id} nft={nft} showTrending={showTrending} />))}
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader currentPage="home" />

      <main className="flex-1 pt-20 pb-24 px-4">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* Hero Section */}
          <PageHero
            title="Flow"
            titleClassName="text-4xl sm:text-5xl lg:text-6xl"
            subtitle="– Decentralized NFT Platform"
            badge={
              <Badge variant="secondary" className="bg-card border border-border px-3 py-1 text-xs">
                <Zap className="w-3 h-3 mr-1 text-primary" />
                Powered by Emon Solana
              </Badge>
            }
          >
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto md:mx-0 leading-relaxed">
              Create, trade, and earn from unique digital assets.{" "}
              <span className="text-primary font-semibold">Start free, play, and monetize</span> your creations.
            </p>

            <div className="mt-3 inline-flex items-center gap-2 py-2 px-4 rounded-full bg-card border border-border max-w-fit">
              <RefreshCw className="w-3 h-3 text-primary animate-spin" style={{ animationDuration: '3s' }} />
              <span className="text-xs font-medium text-foreground">{dynamicMessage}</span>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row items-center gap-3">
              <a
                href="https://raydium.io/swap/?inputMint=sol&outputMint=76kfPfv218KhNQGx7gpC1GR9UWLZHBGSimhyK79ZpUMP"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl gradient-primary text-primary-foreground font-semibold shadow-lg hover:scale-105 transition-transform duration-300"
              >
                <Coins className="w-5 h-5" />
                Swap SOL → EMON
                <ExternalLink className="w-4 h-4" />
              </a>
              <p className="text-xs font-medium text-muted-foreground">
                No platform fees — <span className="text-primary">burn mechanism only</span>
              </p>
            </div>
          </PageHero>

          {/* AirDrop Challenge CTA */}
          <Card className="border-primary/30 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 overflow-hidden">
            <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg font-bold text-foreground">AirDrop Challenge — Win EMON!</h3>
                <p className="text-xs text-muted-foreground">Mint daily, invite friends, climb the leaderboard. Top 5 win up to $50 in EMON tokens.</p>
              </div>
              <Button onClick={() => navigate("/airdrop-challenge")} className="shrink-0 gap-2">
                <Zap className="w-4 h-4" /> Join Now <ArrowRight className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>

          {/* NFT Tabs Section */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-xl mx-auto grid-cols-4 mb-3">
              <TabsTrigger value="trending" className="gap-2 cursor-pointer data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Trending</span>
              </TabsTrigger>
              <TabsTrigger value="recent" className="gap-2 cursor-pointer data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
                <Clock className="w-4 h-4" />
                <span className="hidden sm:inline">Recent</span>
              </TabsTrigger>
              <TabsTrigger value="high-value" className="gap-2 cursor-pointer data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
                <Coins className="w-4 h-4" />
                <span className="hidden sm:inline">High Value</span>
              </TabsTrigger>
              <TabsTrigger value="collections" className="gap-2 cursor-pointer data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
                <FolderOpen className="w-4 h-4" />
                <span className="hidden sm:inline">Collection</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="trending">
              <Card>
                <CardHeader className="pb-2 pt-4">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <div className="p-2 rounded-lg gradient-primary"><TrendingUp className="w-5 h-5 text-white" /></div>
                    <span className="text-primary">Trending NFTs</span>
                    <Badge variant="secondary" className="ml-auto">Hot</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                  {renderNFTSection(trendingNFTs, true)}
                  <div className="flex justify-center mt-4">
                    <Button onClick={() => navigate("/marketplace")} className="gradient-primary text-primary-foreground font-semibold gap-2 hover:scale-105 transition-transform">
                      Explore Marketplace <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="high-value">
              <Card>
                <CardHeader className="pb-2 pt-4">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <div className="p-2 rounded-lg gradient-primary"><Coins className="w-5 h-5 text-white" /></div>
                    <span className="text-primary">High-Value NFTs</span>
                    <Badge variant="secondary" className="ml-auto">Premium</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                  {renderNFTSection(highValueNFTs, false)}
                  <div className="flex justify-center mt-4">
                    <Button onClick={() => navigate("/marketplace")} className="gradient-primary text-primary-foreground font-semibold gap-2 hover:scale-105 transition-transform">
                      Explore Marketplace <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="recent">
              <Card>
                <CardHeader className="pb-2 pt-4">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <div className="p-2 rounded-lg gradient-primary"><Clock className="w-5 h-5 text-white" /></div>
                    <span className="text-primary">Recent</span>
                    <Badge variant="secondary" className="ml-auto">New</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                  {renderNFTSection(recentNFTs, false)}
                  <div className="flex justify-center mt-4">
                    <Button onClick={() => navigate("/marketplace")} className="gradient-primary text-primary-foreground font-semibold gap-2 hover:scale-105 transition-transform">
                      Explore Marketplace <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="collections">
              <div className="space-y-4">
                {collectionNFTs.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No collections available yet</p>
                    </CardContent>
                  </Card>
                ) : (
                  collectionNFTs.map(({ collection, nfts }) => (
                    <Card key={collection.collection_name}>
                      <CardHeader className="pb-2 pt-4">
                        <CardTitle className="flex items-center justify-between text-lg">
                          <span className="text-foreground">{collection.collection_name}</span>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{collection.nft_count} items</span>
                            <span>•</span>
                            <span>{formatVolumeDisplay(collection.total_volume || 0).usd} volume</span>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pb-4">
                        <CollectionCarousel nfts={nfts} collection={collection} />
                      </CardContent>
                    </Card>
                  ))
                )}
                <div className="flex justify-center mt-3">
                  <Button onClick={() => navigate("/marketplace")} className="gradient-primary text-primary-foreground font-semibold gap-2 hover:scale-105 transition-transform">
                    Explore All Collections <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Features Card */}
          <Card>
            <CardContent className="p-4">
              <div className="grid md:grid-cols-3 gap-3 mb-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="p-2 rounded-lg gradient-primary"><Image className="h-4 w-4 text-white" /></div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground">Create NFTs</p>
                    <p className="text-xs text-muted-foreground">Mint your digital art for free</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/5 border border-accent/20">
                  <div className="p-2 rounded-lg bg-accent"><ShoppingBag className="h-4 w-4 text-white" /></div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground">Buy & Sell</p>
                    <p className="text-xs text-muted-foreground">Trade securely on-chain</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="p-2 rounded-lg gradient-primary"><Coins className="h-4 w-4 text-white" /></div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground">Earn EMON</p>
                    <p className="text-xs text-muted-foreground">Bonding curve rewards</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                {connected ? (
                  <>
                    <Button variant="default" size="lg" className="w-full sm:w-auto gap-2 gradient-primary text-primary-foreground" onClick={() => navigate("/dashboard")}>
                      <Sparkles className="h-5 w-5" /> Create Your NFT
                    </Button>
                    <Button variant="outline" size="lg" className="w-full sm:w-auto gap-2" onClick={() => navigate("/marketplace")}>
                      <ShoppingBag className="h-5 w-5" /> Explore Marketplace
                    </Button>
                  </>
                ) : isMobile ? (
                  <Dialog open={showMobileWallet} onOpenChange={setShowMobileWallet}>
                    <DialogTrigger asChild>
                      <Button variant="default" size="lg" className="w-full sm:w-auto gap-2 gradient-primary text-primary-foreground">
                        <Wallet className="h-5 w-5" /> Connect Wallet
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-background border-border p-0">
                      <MobileWalletConnect onClose={() => setShowMobileWallet(false)} />
                    </DialogContent>
                  </Dialog>
                ) : (
                  <WalletMultiButton className="px-8 py-4 text-lg font-semibold">Connect Wallet</WalletMultiButton>
                )}
              </div>

              {!connected && (
                <div className="flex flex-col items-center gap-2 mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">Don't have a wallet? Download one:</p>
                  <div className="flex items-center gap-4">
                    <a href="https://phantom.app/download" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border hover:border-primary/50 transition-all bg-card hover:bg-muted">
                      <svg className="w-5 h-5" viewBox="0 0 128 128" fill="none">
                        <circle cx="64" cy="64" r="64" fill="url(#phantom-gradient)" />
                        <path d="M110.584 64.9142H99.142C99.142 41.7651 80.173 23 56.7724 23C33.6612 23 14.8716 41.3057 14.4118 64.0583C13.936 87.5523 33.1654 107.786 56.8461 108.996C60.4068 109.185 63.9393 108.996 67.4 108.429C69.5335 108.101 71.6388 107.596 73.7159 106.972C79.0352 105.364 83.9518 102.836 88.3071 99.4881C93.3236 95.6262 97.6366 90.9168 101.058 85.5652C101.624 84.6949 102.162 83.8105 102.671 82.912C104.398 79.872 105.789 76.6588 106.834 73.3208C107.594 70.8909 108.174 68.4048 108.553 65.8907C108.668 65.1318 108.553 64.9142 110.584 64.9142Z" fill="url(#phantom-inner)" />
                        <defs>
                          <linearGradient id="phantom-gradient" x1="64" y1="0" x2="64" y2="128" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#534BB1" /><stop offset="1" stopColor="#551BF9" />
                          </linearGradient>
                          <linearGradient id="phantom-inner" x1="62.5" y1="23" x2="62.5" y2="109" gradientUnits="userSpaceOnUse">
                            <stop stopColor="white" /><stop offset="1" stopColor="#F0F0F0" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <span className="text-xs font-medium text-foreground">Phantom</span>
                    </a>
                    <a href="https://solflare.com/download" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border hover:border-primary/50 transition-all bg-card hover:bg-muted">
                      <img src="https://solflare.com/favicon.ico" alt="Solflare" className="w-5 h-5" />
                      <span className="text-xs font-medium text-foreground">Solflare</span>
                    </a>
                    <a href="https://backpack.app/download" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border hover:border-primary/50 transition-all bg-card hover:bg-muted">
                      <img src="https://backpack.app/favicon.ico" alt="Backpack" className="w-5 h-5" />
                      <span className="text-xs font-medium text-foreground">Backpack</span>
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Creators */}
          <TopCreatorsLeaderboard />

          {/* On-Chain Transparency Card */}
          <Card>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="flex items-center justify-center gap-2 text-lg">
                <Code2 className="w-5 h-5 text-primary" />
                On-Chain Transparency
              </CardTitle>
              <p className="text-xs text-muted-foreground text-center">All transactions are recorded on the Solana blockchain.</p>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Smart Contract</div>
                  <ProgramIdDisplay />
                </div>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div><div className="text-xl font-bold text-foreground">{stats.nfts.toLocaleString()}</div><div className="text-xs text-muted-foreground">NFTs</div></div>
                  <div><div className="text-xl font-bold text-foreground">{stats.users.toLocaleString()}</div><div className="text-xs text-muted-foreground">Users</div></div>
                  <div><div className="text-xl font-bold text-green-500">{formatVolumeDisplay(stats.volume).usd}</div><div className="text-xs text-muted-foreground">Volume</div></div>
                  <div><div className="text-xl font-bold text-green-500">100%</div><div className="text-xs text-muted-foreground">On-Chain</div></div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border flex justify-center">
                <EmonPriceIndicator showRefreshButton={true} />
              </div>
            </CardContent>
          </Card>

          {/* Anti-Fraud */}
          <Card className="border-green-500/20">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Shield className="w-4 h-4 text-green-500" />
                <span className="font-semibold text-foreground text-sm">Anti-Fraud Protection</span>
              </div>
              <p className="text-xs text-muted-foreground max-w-2xl mx-auto">All NFTs are verified for originality. Your safety is our priority.</p>
            </CardContent>
          </Card>

          {/* EMON Boost */}
          <Card className="border-primary/30">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl gradient-primary flex items-center justify-center shadow-lg">
                    <Sparkles className="w-6 h-6 md:w-7 md:h-7 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg font-bold text-foreground mb-1">EMON Boost</h3>
                  <p className="text-xs text-muted-foreground mb-2">Boost your social media visibility by rewarding engagement on X and YouTube.</p>
                  <div className="flex flex-wrap gap-1 justify-center md:justify-start">
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-xs">Social</Badge>
                    <Badge variant="outline" className="bg-accent/10 text-accent border-accent/30 text-xs">Earn</Badge>
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-xs">Grow</Badge>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <a href="https://emonboost.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg gradient-primary text-primary-foreground font-semibold text-sm shadow-lg hover:scale-105 transition-transform">
                    <Sparkles className="w-4 h-4" /> Try EMON Boost <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
